import React, { useMemo } from 'react';

export const REMINDER_PRESET_OFFSETS = [10, 30, 60, 1440];

export const REMINDER_PRESETS = [
  { offsetMinutes: 10, label: '10 min' },
  { offsetMinutes: 30, label: '30 min' },
  { offsetMinutes: 60, label: '1 h' },
  { offsetMinutes: 1440, label: '1 d' }
];

export const formatReminderOffsetLabel = (offsetMinutes) => {
  const preset = REMINDER_PRESETS.find((item) => item.offsetMinutes === offsetMinutes);
  if (preset) return preset.label;

  if (offsetMinutes >= 1440 && offsetMinutes % 1440 === 0) {
    const days = offsetMinutes / 1440;
    return days === 1 ? '1 d' : `${days} d`;
  }

  if (offsetMinutes >= 60 && offsetMinutes % 60 === 0) {
    const hours = offsetMinutes / 60;
    return hours === 1 ? '1 h' : `${hours} h`;
  }

  return `${offsetMinutes} min`;
};

const CalendarEventReminderSelector = ({
  selectedOffsets = [],
  existingReminders = [],
  onChange,
  label = 'Recordatorios',
  description = '',
  emptyLabel = 'Sin recordatorios por email'
}) => {
  const selectedSet = useMemo(() => new Set(selectedOffsets), [selectedOffsets]);

  const toggleOffset = (offsetMinutes) => {
    const next = new Set(selectedSet);

    if (next.has(offsetMinutes)) {
      next.delete(offsetMinutes);
    } else {
      next.add(offsetMinutes);
    }

    onChange(Array.from(next).sort((a, b) => a - b));
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <span className="tw-text-sm tw-font-medium tw-text-gray-700">{label}</span>
      {description && (
        <p className="tw-text-xs tw-text-gray-500 tw-leading-relaxed tw-m-0">{description}</p>
      )}
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {REMINDER_PRESETS.map(({ offsetMinutes, label: presetLabel }) => {
          const isSelected = selectedSet.has(offsetMinutes);

          return (
            <button
              key={offsetMinutes}
              type="button"
              onClick={() => toggleOffset(offsetMinutes)}
              className={[
                'tw-rounded-full tw-border tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-transition',
                isSelected
                  ? 'tw-border-blue-500 tw-bg-blue-50 tw-text-blue-700'
                  : 'tw-border-gray-300 tw-bg-white tw-text-gray-600 hover:tw-border-gray-400'
              ].join(' ')}
            >
              {presetLabel}
            </button>
          );
        })}
      </div>
      {selectedOffsets.length === 0 && (
        <span className="tw-text-xs tw-text-gray-500">{emptyLabel}</span>
      )}
    </div>
  );
};

export const buildRemindersAttributes = ({
  selectedOffsets,
  existingReminders = []
}) => {
  const existingByOffset = new Map(
    (existingReminders || []).map((reminder) => [reminder.offset_minutes, reminder])
  );

  const attributes = selectedOffsets.map((offsetMinutes) => {
    const existing = existingByOffset.get(offsetMinutes);
    if (existing) {
      return { id: existing.id, offset_minutes: offsetMinutes };
    }

    return { offset_minutes: offsetMinutes };
  });

  (existingReminders || []).forEach((reminder) => {
    if (!selectedOffsets.includes(reminder.offset_minutes)) {
      attributes.push({
        id: reminder.id,
        offset_minutes: reminder.offset_minutes,
        _destroy: true
      });
    }
  });

  return attributes;
};

export default CalendarEventReminderSelector;
